import {
  Accordion,
  Button,
  Heading,
  Icon,
  IconButton,
  Loading,
  Modal,
  ModalNotice,
  Steps,
  Text,
  HStack,
  VStack,
} from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { ErrorListResponse, Service, instanceOfErrorListResponse } from '@genial/services';
import { Utils as GenialUtils, useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { Box, Modal as NBModal, ScrollView, View } from 'native-base';
import { useContext, useEffect, useRef, useState } from 'react';
import { Pressable } from 'react-native';
import { useRecoilValue } from 'recoil';

import { useToastCreator } from '../../hooks/useToastCreator';
import { ImpersonateMenuContext } from '../../pages/ImpersonateMenu';
import { SignedInNavigationScreenNavigationProp } from '../../routes/types';
import { useCustomerActions } from '../../store/customer/actions';
import { customerItemState, customerStatusState } from '../../store/customer/atom';
import { hasCustomerItem } from '../../store/customer/selector';
import { masterStateSelectedAdvisorSelector } from '../../store/master/selector';
import { useOrderActions } from '../../store/order/actions';
import { orderItemState, orderStatusState } from '../../store/order/atom';
import { useProductActions } from '../../store/product/actions';
import {
  productItemState,
  productStatusState,
  structuredOperationsOptionBillingTypeSelectedState,
} from '../../store/product/atom';
import { hasProductItem } from '../../store/product/selectors';
import { sessionState } from '../../store/session/atom';
import { CockpitSectionBlockingMismatchedPartner } from '../Cockpit/components/CockpitSectionBlockingMismatchedPartner';
import { showCockpit } from '../Cockpit/constants';

import { Footer } from './components/Footer';
import { SectionLocked } from './components/SectionLocked';
import { CustomerStep } from './Customer';
import { OrderStep } from './Order';
import { getOrderNameByType } from './Order/constants';
import { ProductStep } from './Product';
import * as Utils from './utils';

export const NewApplication = () => {
  const firstUpdate = useRef(true);
  const advisorChangeResolver = useRef<((value: boolean) => void) | null>(null);
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { showToast, showToastFromError } = useToastCreator('new-application-toast');
  const { setConfirmationCallback } = useContext(ImpersonateMenuContext);

  const { userProfile } = useRecoilValue(sessionState);
  const isManager = !!(userProfile?.isGestor && userProfile?.idTipoParceiro !== 4);

  const hasCustomer = useRecoilValue(hasCustomerItem);
  const hasProduct = useRecoilValue(hasProductItem);

  const customerItem = useRecoilValue(customerItemState);
  const productItem = useRecoilValue(productItemState);
  const orderItem = useRecoilValue(orderItemState);
  const masterAdvisorSelected = useRecoilValue(masterStateSelectedAdvisorSelector);
  const structuredOperationBillingOptionSelected = useRecoilValue(
    structuredOperationsOptionBillingTypeSelectedState
  );

  const orderStatus = useRecoilValue(orderStatusState);
  const customerStatus = useRecoilValue(customerStatusState);
  const productStatus = useRecoilValue(productStatusState);

  const { resetCustomer, updateCustomerStatus } = useCustomerActions();
  const { resetProduct, updateProductStatus, resetProductFilterAndSearchParams } =
    useProductActions();
  const { resetOrder } = useOrderActions();

  const [modalCancelOrderIsOpen, setModalCancelOrderIsOpen] = useState(false);
  const [isSendingOrder, setIsSendingOrder] = useState(false);
  const [isReturning, setIsReturning] = useState(false);

  const handleCancelOrder = async (forceReturn = false) => {
    resetCustomer();
    resetProduct();
    resetOrder();
    setModalCancelOrderIsOpen(false);

    if (advisorChangeResolver.current) {
      advisorChangeResolver.current(true);
      advisorChangeResolver.current = null;
    } else if (isReturning || forceReturn) {
      navigate('Cockpit');
    }
  };

  const handleEditCustomer = () => {
    const productStatusData = {
      label: hasProduct ? 'Produto selecionado' : 'Selecione o produto',
      status: hasProduct ? 'completed' : 'closed',
    } as const;

    updateCustomerStatus({ label: 'Trocar o cliente', status: 'opened' });
    updateProductStatus(productStatusData);
  };

  const handlePressClosedCustomer = () => {
    updateCustomerStatus({ label: 'Selecione o cliente', status: 'opened' });
    productStatus.status === 'opened' &&
      updateProductStatus({ label: 'Produto selecionado', status: 'closed' });
  };

  const handleEditProduct = () => {
    const customerStatusData = {
      label: hasCustomer ? 'Cliente selecionado' : 'Selecione o cliente',
      status: hasCustomer ? 'completed' : 'closed',
    } as const;
    updateProductStatus({ label: 'Trocar o produto', status: 'opened' });
    updateCustomerStatus(customerStatusData);
  };

  const handlePressClosedProduct = () => {
    customerStatus.status === 'opened' &&
      updateCustomerStatus({
        label: 'Cliente selecionado',
        status: hasCustomer ? 'completed' : 'closed',
      });
    updateProductStatus({ label: 'Selecione o produto', status: 'opened' });
  };

  const currentStep = (): number => {
    if (hasProduct) {
      return 3;
    }
    return hasCustomer ? 2 : 1;
  };

  const currentStepMobile = () => {
    const stepsMobile = {
      1: 'Selecione o cliente',
      2: 'Selecione o produto',
      3: 'Conclua sua boleta',
    };

    let step = 1;

    if (customerStatus.status === 'completed' && productStatus.status !== 'completed') {
      step = 2;
    }

    if (customerStatus.status === 'completed' && productStatus.status === 'completed') {
      step = 3;
    }

    const data = { step, title: stepsMobile[step] as string };

    return data;
  };

  const modalCancelOrderOnClose = () => {
    if (advisorChangeResolver.current) {
      advisorChangeResolver.current(false);
      advisorChangeResolver.current = null;
    }
    setModalCancelOrderIsOpen(false);
  };

  const modalCancelOrderOnOpenByReturn = () => {
    if (hasCustomer) {
      setIsReturning(true);
      return modalCancelOrderOnOpen();
    }
    handleCancelOrder(true);
  };

  const modalCancelOrderOnOpen = () => {
    setModalCancelOrderIsOpen(true);
  };

  const onSendOrder = () => {
    const productType = productItem.current.type;
    const operationType = productItem.current.operationType;
    const operationTypeIsSale = !!(operationType && operationType === 'sell');
    const productId = String(productItem.current.productId || productItem.current.quote?.productId);

    const serviceMethod = Utils.getOrderServiceMethod({
      operationType,
    });

    const params = Utils.getOrderPostParams({
      orderItem,
      structuredOperationBillingOptionSelected,
      isManager,
      productItem,
      productType,
      customerItem,
      productId,
      operationTypeIsSale,
    });

    setIsSendingOrder(true);

    Service[Utils.productServiceTypes[productType]].V1[serviceMethod](params)
      .then(async ({ data }) => {
        if (data) {
          const resumeData = {
            ...data,
            type: productType,
            operationType,
            operationTypeIsSale,
            fullProductItem: productItem.current,
            fullCustomerItem: customerItem.current,
          };

          navigate('Resume', resumeData);
          resetProduct();
          resetOrder();
          resetProductFilterAndSearchParams();
        }
      })
      .catch((error) => {
        setIsSendingOrder(false);

        if (instanceOfErrorListResponse(error)) {
          (error as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }

        showToast(...Utils.defaultErrorMessage);
      })
      .finally(() => setIsSendingOrder(false));
  };

  useEffect(() => {
    if (!modalCancelOrderIsOpen) {
      setIsReturning(false);
    }
  }, [modalCancelOrderIsOpen]);

  useEffect(() => {
    if (!masterAdvisorSelected || firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    handleCancelOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterAdvisorSelected]);

  const productType = productItem.current.type;

  const { handleSelectOrderItem } = useOrderActions();

  useEffect(() => {
    const formattedDate = GenialUtils.Mask.DateFormat(new Date(), 'dd/MM/yyyy');
    handleSelectOrderItem({
      date: formattedDate,
      name: getOrderNameByType(productType),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType]);

  useEffect(() => {
    if (hasCustomer) {
      const confirmationHandler = async () => {
        return new Promise<boolean>((resolve) => {
          advisorChangeResolver.current = resolve;
          setModalCancelOrderIsOpen(true);
        });
      };

      setConfirmationCallback(confirmationHandler);
    } else {
      setConfirmationCallback(undefined);
    }

    return () => {
      if (advisorChangeResolver.current) {
        advisorChangeResolver.current(false);
        advisorChangeResolver.current = null;
      }
      setConfirmationCallback(undefined);
    };
  }, [hasCustomer, setConfirmationCallback]);

  const keepCustomerItem = customerStatus?.status === 'opened' && hasCustomer;
  const keepProductItem = productStatus?.status === 'opened' && hasProduct;

  const handleKeepCustomer = () => {
    updateCustomerStatus({ label: 'Cliente selecionado', status: 'completed' });
    if (!hasProduct) {
      updateProductStatus({ label: 'Selecione o produto', status: 'opened' });
    }
  };

  const handleKeepProduct = () =>
    updateProductStatus({ label: 'Produto selecionado', status: 'completed' });

  const customerAccordion = (
    <Accordion
      testId="qa"
      onPress={handlePressClosedCustomer}
      onEdit={handleEditCustomer}
      title={customerStatus.label}
      status={customerStatus.status}
      keepItem={keepCustomerItem}
      keepItemText="MANTER ESTE CLIENTE"
      onKeepItem={handleKeepCustomer}
      editButtonText="Trocar cliente">
      <CustomerStep />
    </Accordion>
  );

  const productAccordion = (
    <Accordion
      testId="qa"
      onPress={handlePressClosedProduct}
      onEdit={handleEditProduct}
      title={productStatus.label}
      status={productStatus.status}
      keepItem={keepProductItem}
      keepItemText="MANTER ESTE PRODUTO"
      onKeepItem={handleKeepProduct}
      closedTitle="ESCOLHA O PRODUTO"
      editButtonText="Trocar produto">
      <ProductStep />
    </Accordion>
  );

  const orderAccordion = (
    <Accordion
      testId="qa"
      canEditAfterComplete={false}
      title={orderStatus.label}
      status={orderStatus.status}>
      <OrderStep />
    </Accordion>
  );

  const defaultHeader = (
    <HStack alignItems="center" justifyContent="space-between" py={Spacing['inset.md']}>
      <HStack alignItems="center" space={Spacing['inline.xxxs']}>
        {showCockpit && (
          <IconButton
            icon="ArrowLeft"
            testId="return-button"
            shadow={3}
            variant="link"
            onPress={modalCancelOrderOnOpenByReturn}
            iconColor={Color['brand.tertiary.principal']}
            iconSize="xs"
          />
        )}
        <Heading testId="qa" size="h2">
          Nova boleta
        </Heading>
      </HStack>
      <Steps testId="qa" currentStep={currentStep()} totalSteps={3} />
    </HStack>
  );

  const cancelOrderButtonIsDisabled = !hasCustomer;

  const responsiveHeader = useBreakpointValue({
    base: (
      <VStack pt={Spacing['inset.md']} px={Spacing['inline.xxxs']} space={Spacing['inset.nano']}>
        <HStack alignItems="center" justifyContent="space-between">
          <HStack alignItems="center" space={Spacing['inline.xxs']}>
            <Pressable onPress={modalCancelOrderOnOpenByReturn}>
              <Icon
                name="ArrowLeft"
                testId="return-button"
                color={Color['brand.primary.principal']}
                size="xxs"
              />
            </Pressable>
            <Text
              testId="qa"
              variant="semibold"
              lineHeight="distant"
              fontSize={Typography['fontsize.lg']}>
              Nova boleta
            </Text>
          </HStack>

          <Button
            variant="textLink"
            _text={{
              color: cancelOrderButtonIsDisabled ? 'black' : 'feedback.error.default',
              fontWeight: 'semibold',
            }}
            isDisabled={cancelOrderButtonIsDisabled}
            onPress={modalCancelOrderOnOpen}>
            Cancelar
          </Button>
        </HStack>
        <Steps
          testId="qa"
          currentStep={currentStepMobile().step}
          totalSteps={3}
          title={currentStepMobile().title}
        />
      </VStack>
    ),
    sm: defaultHeader,
  });

  const customerStep = useBreakpointValue({
    base: <CustomerStep />,
    sm: customerAccordion,
  });

  const productStep = useBreakpointValue({
    base: !hasCustomer ? <SectionLocked title="Selecione o produto" /> : <ProductStep />,
    sm: productAccordion,
  });

  const orderStep = useBreakpointValue({
    base: <OrderStep />,
    sm: orderAccordion,
  });

  return (
    <>
      <Box flex={1}>
        <ScrollView>
          <View
            h="full"
            width="100%"
            minW="container"
            flex={1}
            px={{ base: 0, sm: Spacing['inline.sm'], lg: Spacing['inline.sm'] }}>
            {responsiveHeader}
            <VStack zIndex={1} space={{ base: Spacing['inline.xxxs'], sm: Spacing['inline.xxs'] }}>
              {customerStep}
              <Box zIndex={10}>{productStep}</Box>
              {!isManager && orderStep}
            </VStack>
            <Footer onCancel={modalCancelOrderOnOpen} onSend={onSendOrder} />
          </View>
        </ScrollView>

        <Modal
          isOpen={modalCancelOrderIsOpen}
          onClose={modalCancelOrderOnClose}
          testId="modal-notice">
          <NBModal.Content
            alignItems="center"
            w="full"
            maxW={{ base: 'full', lg: '540px' }}
            marginBottom={{ base: 0, lg: 'none' }}
            marginTop={{ base: 'auto', lg: 0 }}>
            <ModalNotice
              type="warning"
              testId="modal-notice"
              onClose={modalCancelOrderOnClose}
              onConfirm={() => handleCancelOrder()}
            />
          </NBModal.Content>
        </Modal>

        <Loading
          isVisible={isSendingOrder}
          testId="qa"
          title="Aguarde enquanto estamos processando seu envio de ordem."
          description="Este processo pode levar alguns segundos, não saia desta pagina."
        />
      </Box>
      <CockpitSectionBlockingMismatchedPartner />
    </>
  );
};
